import { ColDef, RowNode } from 'ag-grid-community';
import { AgGridColumn } from 'ag-grid-react/lib/agGridColumn';
import React, { forwardRef, PropsWithRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { HcpGrid, HcpRequestStateUI, MessagesFilterTypes } from '.';
import { API_URL, useThreads } from '../api';
import { MessagesFilters } from '../components/MessagesFilters';
import { AnyType, Message } from '../interfaces';
import { AppRootState } from '../redux';
import { useState } from 'react';
import {
  ActionColumnActions,
  actionsColumnDefinition,
  clientApplicationNameSelector,
  messageDateColumnDefinition,
  generalColumnDefinition,
  featureGridColumnsSelector,
  getFeatureGridColumnProps,
  messageColumnDefinition,
  messageDirectionColumnDefinition,
  getConfigurationDataKey,
  ROUTE_PATHS
} from '../utils';

export interface MessagesTableComponent {
  loadData(): void;
}

interface MessagesTableProps extends PropsWithRef<AnyType> {
  searchText?: string;
  messages?: Message[];
}

const columnSelector = featureGridColumnsSelector(getConfigurationDataKey('messages.table'));

export const MessagesTable = forwardRef<AnyType, MessagesTableProps>((props: MessagesTableProps, ref) => {
  const currentUser = useSelector((state: AppRootState) => state.auth?.session?.user);
  const [selectedFilter, setSelectedFilter] = useState<string>();
  const { data: messages, ...result } = useThreads({
    base: API_URL,
    lazy: true
  });

  const applicationName = useSelector(clientApplicationNameSelector);
  const loadData = useCallback(() => {
    if (!currentUser) return;

    result.refetch({
      queryParams: {
        applicationName: applicationName
      }
    });
  }, [currentUser]);

  useEffect(loadData, [loadData]);

  useImperativeHandle(
    ref,
    () => ({
      loadData
    }),
    [loadData]
  );

  const columns = useSelector(columnSelector);

  if (!columns) return null;

  const filterGridComparator = (
    node: RowNode,
    { filterState: value, searchText }: { filterState: string | undefined; searchText: string | undefined },
    columns?: ColDef[]
  ) => {
    const hasSearch =
      !searchText ||
      (searchText &&
        !!columns?.find(
          (col: AnyType) => col.field && node.data[col.field]?.toLowerCase()?.includes(searchText?.toLowerCase())
        ));

    switch (value) {
      case MessagesFilterTypes.unread:
        return !node.data.isRead && hasSearch;
      case MessagesFilterTypes.read:
        return node.data.isRead && hasSearch;
      default:
        return hasSearch;
    }
  };

  return (
    <div>
      <MessagesFilters onFilterChange={setSelectedFilter} />
      <div className='mb-3 site-background'>
        <HcpRequestStateUI
          loadingUIEnabled
          isLoading={result.loading || !messages}
          errorUIEnabled
          errorText={messages?.errors?.map((e) => e.description).join(' ')}
          errorTitle='Messages Error'
          isError={result.error || (messages?.errors?.length ?? 0) > 0}
          reloadingEnabled
          reload={loadData}>
          <HcpGrid
            isFilterActive={selectedFilter !== 'all' || !!props.searchText}
            filterComparator={filterGridComparator}
            filterValue={{ filterState: selectedFilter, searchText: props.searchText }}
            gridOptions={{
              getRowStyle: (params: any) => (!params.data.isRead == true ? { fontWeight: '700' } : '')
            }}
            rowData={messages?.messageThreads ?? []}
            // rowSelection='single'
            className='grid-wrapper'
            pagination
            suppressCellSelection={true}>
            <AgGridColumn resizable={false} {...messageDirectionColumnDefinition()} />
            <AgGridColumn
              resizable={true}
              {...messageDateColumnDefinition('lastMessageDate')}
              {...getFeatureGridColumnProps(columns.date)}
            />
            <AgGridColumn
              {...generalColumnDefinition()}
              field='patientName'
              resizable={true}
              {...getFeatureGridColumnProps(columns.patientName)}
            />
            <AgGridColumn
              {...generalColumnDefinition()}
              field='providerName'
              resizable={true}
              {...getFeatureGridColumnProps(columns.prescriberName)}
            />
            <AgGridColumn
              {...generalColumnDefinition()}
              field='subject'
              resizable={true}
              {...getFeatureGridColumnProps(columns.subject)}
            />
            <AgGridColumn
              {...messageColumnDefinition()}
              resizable={true}
              {...getFeatureGridColumnProps(columns.messageDetail)}
              headerComponentParams={{
                menuIcon: '<span class="ag-icon ag-icon-small-down"></span>' // Use a custom icon
              }}
            />
            <AgGridColumn
              field='sender'
              resizable={true}
              {...generalColumnDefinition()}
              {...getFeatureGridColumnProps(columns.from)}
              headerComponentParams={{
                menuIcon: '<span class="ag-icon ag-icon-small-down"></span>'
              }}
            />
            <AgGridColumn
              field='assignedTo'
              {...generalColumnDefinition()}
              resizable={true}
              {...getFeatureGridColumnProps(columns.to)}
            />
            <AgGridColumn
              resizable={false}
              {...actionsColumnDefinition((node: RowNode): ActionColumnActions[] => [
                {
                  href: `${ROUTE_PATHS.messages}/${node.data.threadID}`,
                  label: 'View Message',
                  icon: <i className='fa-light fa-envelope'></i>
                },
                {
                  href: `${ROUTE_PATHS.messages}/${node.data.threadID}#reply`,
                  label: 'Reply',
                  icon: <i className='fa-regular fa-reply'></i>
                }
              ])}
            />
          </HcpGrid>
        </HcpRequestStateUI>
      </div>
    </div>
  );
});

MessagesTable.displayName = 'MessagesTable';
